import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BellRing, MailPlus, UserPlus } from 'lucide-react';
import {
  canInvite, canRemind, canRemindPerson, childrenOf, isElement, isOpenSpot, layoutDay, nodeLabel, nodeTitle, pendingSuggestionOf,
  pick, placeOf, personById, removedAssignmentsOf, roomName, shownAssignmentsOf, subtreeNeedsAttention, timeRange,
  venueText,
} from './programAdminModel';
import { OpenSpotTag, RoleText, StateTag, SuggestedTag } from './StateTag';
import {
  BlockCard, BlockHead, BlockKind, BlockMeta, BlockStack, BlockTitle, ChildRow, ChildStack, ElementHeading,
  ElementQuote, ElementText, EmptyDay, EntryLabel, InternalTag, MiniButton, MiniButtonPrimary, NameCell, OpenLabel,
  RemovedCard, RemovedTag, RoleCell, RoomTag, SlotRow, SlotTime, SpotActions, SpotCheck, SpotGroup, SpotList,
  SpotName, SpotNote, SpotRow, SpotTags,
} from './programAdminStyles';

const QUIET_KINDS = new Set(['BREAK', 'ARRIVAL', 'SOCIAL']);

const TBA_MESSAGE = { id: 'programAdmin.tba', defaultMessage: 'TBA' };
const REMOVED_MESSAGE = { id: 'programAdmin.removed', defaultMessage: 'Removed' };

// A display element (design §4.4.1) in its place among the entries. The admin sees the same kinds
// and styles the public program prints, just denser.
const DisplayElement = ({ element, language }) => {
  const text = pick(language, element.textDe, element.textEn);
  if (!text) return null;
  const box = element.style === 'HIGHLIGHTED_BOX';
  if (element.kind === 'QUOTE') return <ElementQuote $box={box}>{text}</ElementQuote>;
  if (element.kind === 'HEADING') return <ElementHeading $box={box}>{text}</ElementHeading>;
  return <ElementText $box={box}>{text}</ElementText>;
};

// The single contextual action a spot shows in its own row, so inviting (or reminding) is one click
// from the spot (plan §7.7 item 5). Everything else lives in the side panel. The rules are the ones
// the backend applies: an invitation needs a person and one of the four states it may start from; a
// reminder needs an open invitation and a person whose reminders are not stopped.
const rowActionOf = ({ assignment, entry, person, actions, intl }) => {
  if (canInvite(assignment)) {
    return (
      <MiniButton
        onClick={() => actions.inviteSpot?.(assignment, entry)}
        aria-label={intl.formatMessage({ id: 'programAdmin.spot.inviteNamed', defaultMessage: 'Invite {name}' }, { name: assignment.personName })}
      >
        <MailPlus size={12} aria-hidden="true" />
        <FormattedMessage id="programAdmin.spot.invite" defaultMessage="Invite" />
      </MiniButton>
    );
  }
  if (canRemind(assignment)) {
    // A reminder needs an anchor: an open invitation whose reminders are not stopped.
    const stopped = !canRemindPerson(person);
    return (
      <MiniButton
        disabled={stopped}
        title={stopped ? intl.formatMessage({ id: 'programAdmin.spot.remindersStopped', defaultMessage: 'Their reminders are stopped - resume them first (People area).' }) : undefined}
        onClick={stopped ? undefined : () => actions.remindSpot?.(assignment, entry)}
      >
        <BellRing size={12} aria-hidden="true" />
        <FormattedMessage id="programAdmin.spot.remind" defaultMessage="Remind" />
      </MiniButton>
    );
  }
  if (assignment.personId == null) {
    return (
      <MiniButtonPrimary onClick={() => actions.fillSpot?.(assignment, entry)}>
        <UserPlus size={12} aria-hidden="true" />
        <FormattedMessage id="programAdmin.spot.fill" defaultMessage="Fill spot" />
      </MiniButtonPrimary>
    );
  }
  return null;
};

// One spot: the checkbox for the bulk invite, the role, the person (or the label printed while
// nobody is on it), its state and its one-click action.
export const SpotLine = ({ assignment, entry, checked, selected, onToggleChecked, onSelectSpot, actions, person }) => {
  const intl = useIntl();
  const removed = !!assignment.removedAt;
  const open = isOpenSpot(assignment);
  const suggestion = pendingSuggestionOf(assignment);
  const name = assignment.personName || assignment.placeholderLabel || intl.formatMessage(TBA_MESSAGE);
  const roleLabel = intl.formatMessage({ id: `programAdmin.role.${assignment.role}`, defaultMessage: assignment.role });
  // Inviting is a bulk action: only a spot the backend would accept may be ticked (InvitationController
  // refuses an open spot, a CONFIRMED and a removed assignment; an INVITED one gets a reminder).
  const selectable = canInvite(assignment);
  return (
    <SpotRow $open={open && !removed} $removed={removed} $selected={selected} data-assignment-id={assignment.id}>
      <SpotCheck
        type="checkbox"
        checked={!!checked}
        disabled={!selectable}
        title={selectable ? undefined : intl.formatMessage({ id: 'programAdmin.spot.notSelectable', defaultMessage: 'Only a spot that can be invited is selectable.' })}
        onChange={() => onToggleChecked?.(assignment.id)}
        aria-label={intl.formatMessage({ id: 'programAdmin.spot.select', defaultMessage: 'Select {role} {name} on {entry}' }, { role: roleLabel, name, entry: nodeTitle(entry, intl.locale) })}
      />
      <RoleCell><RoleText role={assignment.role} /></RoleCell>
      <NameCell>
        {assignment.groupLabel && !removed && <SpotGroup>{assignment.groupLabel}</SpotGroup>}
        {assignment.personId != null ? (
          <SpotName type="button" onClick={() => onSelectSpot?.(assignment, entry)}>{assignment.personName}</SpotName>
        ) : (
          <OpenLabel type="button" onClick={() => onSelectSpot?.(assignment, entry)}>{name}</OpenLabel>
        )}
        {assignment.displayNote && <SpotNote>{assignment.displayNote}</SpotNote>}
        {assignment.personId != null && assignment.placeholderLabel && <SpotNote>({assignment.placeholderLabel})</SpotNote>}
      </NameCell>
      <SpotTags>
        <StateTag status={assignment.status} />
        {open && !removed && <OpenSpotTag />}
        {open && !removed && suggestion && <SuggestedTag suggestion={suggestion} />}
        {removed && <RemovedTag>{intl.formatMessage(REMOVED_MESSAGE)}</RemovedTag>}
      </SpotTags>
      <SpotActions>
        {!removed && actions && rowActionOf({ assignment, entry, person, actions, intl })}
      </SpotActions>
    </SpotRow>
  );
};

// The spots of one entry, in order, with the removed ones after them while "show removed" is on.
const EntrySpots = ({ entry, showRemoved, filterAttention, ctx }) => {
  const rows = shownAssignmentsOf(entry, filterAttention, ctx.people);
  const removed = showRemoved ? removedAssignmentsOf(entry) : [];
  if (rows.length === 0 && removed.length === 0) return null;
  return (
    <SpotList>
      {rows.map((a) => (
        <li key={a.id}>
          <SpotLine
            assignment={a}
            entry={entry}
            checked={ctx.checked.has(a.id)}
            selected={ctx.selection?.kind === 'spot' && ctx.selection.id === a.id}
            onToggleChecked={ctx.onToggleChecked}
            onSelectSpot={ctx.onSelectSpot}
            actions={ctx.actions}
            person={personById(ctx.people, a.personId)}
          />
        </li>
      ))}
      {removed.map((a) => (
        <li key={a.id}>
          <SpotLine assignment={a} entry={entry} checked={false} selected={false} onSelectSpot={ctx.onSelectSpot} />
        </li>
      ))}
    </SpotList>
  );
};

// The clickable header of an entry: kind, label, title, time, room and venue, plus the flags the
// admin has to see without opening the panel (internal, removed).
const EntryHead = ({ entry, program, language, selected, removed, onSelectEntry }) => {
  const intl = useIntl();
  const { room, venue } = placeOf(program, entry.roomId);
  const label = nodeLabel(entry, language);
  const range = entry.depth === 0 ? timeRange(entry.startTime, entry.endTime) : '';
  return (
    <BlockHead type="button" onClick={() => onSelectEntry?.(entry)} aria-pressed={!!selected}>
      <BlockKind>{entry.kind}</BlockKind>
      {label && <EntryLabel>{label}</EntryLabel>}
      <BlockTitle $depth={entry.depth}>{nodeTitle(entry, language)}</BlockTitle>
      {range && <BlockMeta>{range}</BlockMeta>}
      {entry.depth === 0 && room && <RoomTag>{roomName(language, room)}</RoomTag>}
      {entry.depth === 0 && venue && (
        <BlockMeta>
          {intl.formatMessage({ id: 'programAdmin.atVenue', defaultMessage: 'at {venue}' }, { venue: venueText(venue) })}
        </BlockMeta>
      )}
      {entry.visibility === 'INTERNAL' && (
        <InternalTag><FormattedMessage id="programAdmin.internal" defaultMessage="Internal" /></InternalTag>
      )}
      {removed && <RemovedTag>{intl.formatMessage(REMOVED_MESSAGE)}</RemovedTag>}
    </BlockHead>
  );
};

// One entry of the tree: its header, its own spots, and everything that hangs off it (the tree is
// at most three levels deep, design §4.4).
const EntryNode = ({ entry, program, language, ctx }) => {
  const removed = !!entry.removedAt;
  const selected = ctx.selection?.kind === 'entry' && ctx.selection.id === entry.id;
  const Card = removed ? RemovedCard : BlockCard;
  const series = entry.depth === 0 ? pick(language, entry.seriesLabelDe, entry.seriesLabelEn) : '';
  const focus = entry.depth === 0 ? pick(language, entry.focusDe, entry.focusEn) : '';
  const children = childrenOf(entry);
  return (
    <Card $quiet={QUIET_KINDS.has(entry.kind) && !removed} $selected={selected && !removed} data-entry-id={entry.id}>
      <EntryHead entry={entry} program={program} language={language} selected={selected} removed={removed} onSelectEntry={ctx.onSelectEntry} />
      {(series || focus) && <BlockMeta>{[series, focus].filter(Boolean).join(' · ')}</BlockMeta>}
      <EntrySpots
        entry={entry}
        showRemoved={ctx.showRemoved}
        filterAttention={ctx.needsAttentionOnly && !removed}
        ctx={ctx}
      />
      {children.length > 0 && (
        <ChildStack $depth={entry.depth + 1}>
          {children.map((child) => (isElement(child) ? (
            <DisplayElement key={`el-${child.id}`} element={child} language={language} />
          ) : (
            <ChildRow key={child.id} $selected={ctx.selection?.kind === 'entry' && ctx.selection.id === child.id}>
              <EntryNode entry={child} program={program} language={language} ctx={ctx} />
            </ChildRow>
          )))}
        </ChildStack>
      )}
    </Card>
  );
};

// The list of one day: the timed slots, then the untimed blocks per room, then - behind the
// "show removed" switch - what was taken out, greyed and labelled.
const ProgramList = ({
  program, language, dayId, checked = new Set(), selection = null, needsAttentionOnly = false,
  showRemoved = false, people = [], onSelectEntry, onSelectSpot, onToggleChecked, actions,
}) => {
  const intl = useIntl();
  const { before, slots, untimed, removed } = layoutDay(program, dayId);
  const ctx = { checked, selection, needsAttentionOnly, showRemoved, people, onSelectEntry, onSelectSpot, onToggleChecked, actions };

  const shownSlots = needsAttentionOnly
    ? slots
      .map((slot) => ({ ...slot, entries: slot.entries.filter((e) => subtreeNeedsAttention(e, people)) }))
      .filter((slot) => slot.entries.length > 0)
    : slots;
  const shownUntimed = needsAttentionOnly
    ? untimed
      .map((group) => ({ ...group, items: group.items.filter((i) => subtreeNeedsAttention(i.entry, people)) }))
      .filter((group) => group.items.length > 0)
    : untimed;
  const empty = shownSlots.length === 0 && shownUntimed.length === 0;

  return (
    <div>
      {!needsAttentionOnly && before.map((el) => <DisplayElement key={`el-${el.id}`} element={el} language={language} />)}
      {empty && (
        <EmptyDay>
          {needsAttentionOnly
            ? <FormattedMessage id="programAdmin.filter.nothingNeedsAttention" defaultMessage="Nothing on this day needs attention." />
            : <FormattedMessage id="programAdmin.day.empty" defaultMessage="Nothing on this day yet." />}
        </EmptyDay>
      )}
      {shownSlots.map((slot) => (
        <React.Fragment key={slot.time}>
          <SlotRow>
            <SlotTime>{slot.time}</SlotTime>
            <BlockStack>
              {slot.entries.map((entry) => (
                <EntryNode key={entry.id} entry={entry} program={program} language={language} ctx={ctx} />
              ))}
            </BlockStack>
          </SlotRow>
          {!needsAttentionOnly && slot.after.map((el) => <DisplayElement key={`el-${el.id}`} element={el} language={language} />)}
        </React.Fragment>
      ))}
      {shownUntimed.map((group) => {
        const { room } = placeOf(program, group.roomId);
        return (
          <div key={group.roomId ?? 'none'}>
            <BlockMeta as="div" style={{ margin: '.9rem 0 .4rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
              {room ? roomName(language, room) : intl.formatMessage({ id: 'programAdmin.noRoom', defaultMessage: 'Without a room' })}
            </BlockMeta>
            <BlockStack>
              {group.items.map(({ entry }) => (
                <EntryNode key={entry.id} entry={entry} program={program} language={language} ctx={ctx} />
              ))}
            </BlockStack>
          </div>
        );
      })}
      {showRemoved && removed.length > 0 && (
        <>
          <BlockMeta as="div" style={{ margin: '1.3rem 0 .4rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
            <FormattedMessage id="programAdmin.removedGroup" defaultMessage="Removed ({count})" values={{ count: removed.length }} />
          </BlockMeta>
          <BlockStack>
            {removed.map((node) => (isElement(node) ? (
              <DisplayElement key={`el-${node.id}`} element={node} language={language} />
            ) : (
              <EntryNode key={node.id} entry={node} program={program} language={language} ctx={{ ...ctx, needsAttentionOnly: false, showRemoved: true }} />
            )))}
          </BlockStack>
        </>
      )}
    </div>
  );
};

export default ProgramList;
